import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  Info,
  BackgroundBox,
  FloatRight,
  PrintButton,
  Icon,
} from './basic';
import BoxedLink from './BoxedLink';
import ServiceDetails from './ServiceDetails';
import ContactInfoRow from './ContactInfoRow';
import { faPrint } from '@fortawesome/free-solid-svg-icons';

const PrintListButton = styled(PrintButton).attrs({
  'aria-label': 'tulosta kaikki palveluntarjoajat',
})``;

const getFoundInfo = (count) =>
  count === 1
    ? 'Löysimme yhden hakuasi vastaavan palveluntarjoajan'
    : `Löysimme ${count} hakuasi vastaavaa palveluntarjoajaa`;

const FoundInfo = styled.h3`
  margin-top: 32px;
`;

const TitleArea = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  justify-content: end;
  @media (min-width: 800px) {
    flex-direction: row;
  }
`;
const Title = styled.h4`
  color: black;
  font-weight: bold;
  flex-grow: 1;
  margin: 0 0 16px;
  font-size: 1.2rem;
`;
const Contact = styled.div`
  font-size: 0.9em;
  font-weight: 600;
  max-width: 100%;
  @media (min-width: 800px) {
    margin-left: 32px;
  }
`;
const Summary = styled.div`
  margin: 0 0 16px;
`;

const DetailWrapper = styled.div`
  margin: 24px 0;
`;

const AvailableTitle = styled.div`
  font-weight: bold;
  margin: 16px 0 4px;
`;

const TopMargin = styled.div`
  margin-top: 24px;
`;

const OrganizationList = React.forwardRef(
  ({ services, organizations, activeSelections }, ref) => {
    const [serviceBuckets, setServiceBuckets] = useState(
      {},
    );

    useEffect(() => {
      const newBuckets = {};

      services.forEach((service) => {
        const organizationId = service.organizations[0];
        if (
          organizationId &&
          service.showInOrganizationViews
        ) {
          // Add services to buckets by organizations
          if (Array.isArray(newBuckets[organizationId])) {
            newBuckets[organizationId].push(service);
          } else {
            newBuckets[organizationId] = [service];
          }
        }
      });
      setServiceBuckets(newBuckets);
    }, [services, organizations, activeSelections]);

    function renderAvailableSercives(
      serviceList,
      organizationId,
    ) {
      return (
        <>
          {!!activeSelections.length &&
            [...new Set(serviceList)]
              .filter(
                (service) =>
                  service.showInOrganizationViews,
              )
              .sort((a, b) =>
                (a.name || '').localeCompare(b.name || ''),
              )
              .map((item) => (
                <DetailWrapper key={item.id}>
                  <ServiceDetails
                    data={item}
                    view={{
                      name: 'inline',
                      summary: 'trimmed',
                    }}
                  />
                </DetailWrapper>
              ))}
          <AvailableTitle>
            Saatavilla olevat palvelut
          </AvailableTitle>
          {organizationId in organizations
            ? organizations[
                organizationId
              ].availableServices
                .sort((a, b) => a.localeCompare(b))
                .join(', ')
            : '–'}
        </>
      );
    }

    function printSelections(evt) {
      window.print();
    }

    return (
      <div ref={ref}>
        <FoundInfo id="found-info">
          {getFoundInfo(Object.keys(serviceBuckets).length)}
          <FloatRight>
            <PrintListButton onClick={printSelections}>
              <Icon icon={faPrint} />
              Tulosta
            </PrintListButton>
          </FloatRight>
        </FoundInfo>

        {serviceBuckets &&
        Object.keys(serviceBuckets).length === 0 &&
        serviceBuckets.constructor === Object ? (
          <p>
            Palveluita ei löytynyt valituilla rajauksilla
          </p>
        ) : null}

        {Object.entries(serviceBuckets)
          .sort((a, b) =>
            (organizations[a[0]]?.name || '').localeCompare(
              organizations[b[0]]?.name || '',
            ),
          )
          .map(([organizationId, serviceList]) => {
            const organization =
              organizations[organizationId];
            const webPage = organization?.webPages?.[0];
            const phone = organization?.phoneNumbers?.[0];
            const summary = organization?.summary;
            return (
              <BackgroundBox key={organizationId}>
                <TitleArea>
                  <Title>{organization?.name}</Title>

                  {!!webPage && (
                    <Contact>
                      <ContactInfoRow
                        value={webPage.url}
                        type="link"
                      />
                    </Contact>
                  )}
                  {!!phone && (
                    <Contact>
                      <ContactInfoRow
                        value={phone.number}
                        type="phone"
                      />
                    </Contact>
                  )}
                </TitleArea>
                {!!summary && <Summary>{summary}</Summary>}
                <Info>
                  {renderAvailableSercives(
                    serviceList,
                    organizationId,
                  )}
                </Info>
                <TopMargin className="organization-link">
                  <BoxedLink
                    to={`/organisaatio?id=${organizationId}`}
                  >
                    Avaa tarkemmat tiedot
                  </BoxedLink>
                </TopMargin>
              </BackgroundBox>
            );
          })}
      </div>
    );
  },
);

export default OrganizationList;
